import { ArrowRight, BadgeCheck, Cpu, Gem, Landmark, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const missionHighlights = [
  {
    icon: Gem,
    label: "Unique Gems",
    detail: "A refined database where quality outranks quantity and every listing signal is designed to feel legitimate and high-value.",
  },
  {
    icon: Cpu,
    label: "Alpha + Sovereignty",
    detail: "Buyers gain intelligence, agents gain control, and both sides move with clearer incentives and less friction.",
  },
  {
    icon: ShieldCheck,
    label: "Professional Integrity",
    detail: "Noise is removed so real inventory, real expertise, and real next steps can remain the focal point of the transaction.",
  },
];

const mvictoLinkClass = "font-semibold text-[#001935] underline decoration-[#D4AF37]/55 underline-offset-4 transition hover:text-[#8b6d16] hover:decoration-[#D4AF37]";

export const AboutPage = () => {
  useSeo({
    title: "About SG Prop Portal | Discovery, Intelligence, and Sovereignty",
    description: "Learn the mission behind SGPropPortal.com, its AI-native technology stack, agent sovereignty commitment, and the role of Mvicto Technologies in architecting the platform.",
  });

  return (
    <div className="bg-[radial-gradient(circle_at_top,#fbf7ee_0%,#f8f3e7_44%,#eef2f6_100%)] text-[#001935]" data-testid="about-page-root">
      <section className="section-shell pb-8 pt-10" data-testid="about-hero-section">
        <div className="mx-auto max-w-4xl text-left md:text-center">
          <p className="text-xs font-bold uppercase tracking-[0.26em] text-[#8b6d16]">About SG Prop Portal</p>
          <h1 className="mt-5 text-4xl font-semibold leading-tight text-[#001935] sm:text-5xl lg:text-6xl" data-testid="about-page-heading">
            Transforming Singapore property search from filtering into discovery.
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-base leading-8 text-[#3f526b] md:text-lg" data-testid="mission-text">
            Our Mission is to transform the Singapore property journey from a chore of filtering into a process of discovery. By equipping Buyers with the <span className="font-semibold text-[#001935]">Alpha</span> (Intelligence) and Agents with the <span className="font-semibold text-[#001935]">Sovereignty</span> (Control), SGPropPortal.com serves as the infrastructure for the most efficient, transparent, and professional real estate transactions in the region.
          </p>
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-3" data-testid="about-hero-highlights-grid">
          {missionHighlights.map(({ icon: Icon, label, detail }) => (
            <div className="rounded-[28px] border border-[#001935]/10 bg-white/86 px-5 py-5 shadow-[0_20px_50px_rgba(0,25,53,0.06)]" data-testid={`about-hero-highlight-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={label}>
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#fff7e2] text-[#8b6d16]">
                  <Icon className="h-5 w-5" />
                </div>
                <p className="text-base font-semibold text-[#001935]">{label}</p>
              </div>
              <p className="mt-4 text-sm leading-7 text-[#4c617c]">{detail}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section-shell py-8">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-10 md:gap-16 md:items-center" data-testid="about-mission-split-section">
          <div className="md:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">The mission</p>
            <h2 className="mt-4 text-3xl font-semibold leading-tight text-[#001935] md:text-4xl">
              Eliminate the noise. Increase the signal.
            </h2>
            <div className="mt-6 space-y-5 text-sm leading-8 text-[#42566f] md:text-base">
              <p>
                SGPropPortal.com was established to solve the primary frustration of the Singapore real estate market: the noise of thousands of duplicate and expired listings. We believe that property search should not be a chore of filtering out data; it should be a process of discovering opportunity. By curating a refined database of <span className="font-semibold text-[#001935]">Unique Gems</span>, we provide a high-definition view of the market where quality always outranks quantity, ensuring every signal on our platform is a legitimate, high-value asset.
              </p>
              <p>
                Most property portals prioritize volume over value, forcing buyers to sift through thousands of duplicate listings, ghost ads, and expired data. This noise creates an illusion of choice while hiding the actual market signal. SGPropPortal.com was built to change the physics of the search. We replace the clutter with a curated collection of Unique Gems—ensuring every listing you see is real, verified, and high-potential.
              </p>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="overflow-hidden rounded-[30px] border border-[#D4AF37]/25 bg-white/80 shadow-[0_20px_60px_rgba(0,25,53,0.08)]">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  alt="Singapore architecture representing the move from property noise to focused discovery"
                  className="h-full w-full object-cover transition duration-500 hover:scale-[1.03]"
                  data-testid="about-mission-image"
                  src="https://images.unsplash.com/photo-1599912694265-70a9c114018e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxzaW5nYXBvcmUlMjBsdXh1cnklMjBhcmNoaXRlY3R1cmUlMjBza3lsaW5lfGVufDB8fHxibHVlfDE3Nzc0NjI5MDV8MA&ixlib=rb-4.1.0&q=85"
                />
              </div>
              <div className="border-t border-[#D4AF37]/16 px-5 py-5">
                <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Discovery over clutter</p>
                <p className="mt-3 text-sm leading-7 text-[#42566f]">A market view where buyers spend less time filtering and more time identifying real opportunity.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell py-8" data-testid="technology-section">
        <div className="overflow-hidden rounded-[36px] border border-[#D4AF37]/16 bg-[linear-gradient(135deg,#08192f_0%,#102947_46%,#173656_100%)] shadow-[0_26px_80px_rgba(0,25,53,0.18)]">
          <div className="grid grid-cols-1 gap-12 px-8 py-10 md:grid-cols-10 md:items-center md:gap-16 md:px-10 md:py-12">
            <div className="md:col-span-3">
              <div className="overflow-hidden rounded-[28px] border border-[#D4AF37]/18 bg-white/5">
                <img
                  alt="Abstract gold technology visual representing AI-native property intelligence"
                  className="aspect-[4/5] h-full w-full object-cover transition duration-500 hover:scale-[1.03]"
                  data-testid="about-technology-image"
                  src="https://images.unsplash.com/photo-1773053525984-3857c1e0be44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGdvbGQlMjBsaW5lcyUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzc3NDU3NzczfDA&ixlib=rb-4.1.0&q=85"
                />
              </div>
            </div>

            <div className="md:col-span-7">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D4AF37]">The Technology — Intelligence as Standard</p>
              <h2 className="mt-4 text-3xl font-semibold leading-tight text-[#F9F8F6] md:text-4xl">
                An AI-native ecosystem built for decision quality.
              </h2>
              <div className="mt-6 space-y-5 text-sm leading-8 text-[#e6dcc4] md:text-base">
                <p>
                  We have moved beyond static listings into an AI-native ecosystem. Central to this is <span className="font-semibold text-white">The Signature Advisor</span>—an institutional-grade AI engine designed to function as a digital associate for every user.
                </p>
                <p>
                  From real-time investment memos to 2026 ABSD/BSD scenario testing, our platform provides the quantitative evaluation tools usually reserved for private equity firms, making professional-level market intelligence accessible to every agent and buyer on our network.
                </p>
              </div>
              <div className="mt-8 grid gap-4 md:grid-cols-2">
                {[
                  { label: "AI-native workflow", value: "Real-time memos, scenario testing, and district intelligence." },
                  { label: "Institutional lens", value: "Private-equity style tools made usable for buyers and frontline advisors." },
                ].map((item) => (
                  <div className="rounded-[24px] border border-[#D4AF37]/14 bg-white/6 px-5 py-5" data-testid={`about-technology-card-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={item.label}>
                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{item.label}</p>
                    <p className="mt-3 text-sm leading-7 text-[#f0e7d2]">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell py-8" data-testid="agent-sovereignty-section">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-10 md:items-center md:gap-16">
          <div className="md:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">The Commitment — Agent Sovereignty</p>
            <h2 className="mt-4 text-3xl font-semibold leading-tight text-[#001935] md:text-4xl">
              Protect the agent signal. Shorten the handshake.
            </h2>
            <div className="mt-6 space-y-5 text-sm leading-8 text-[#42566f] md:text-base">
              <p>
                We believe elite agents should own their digital identity and their leads. Unlike traditional portals that aggregate and dilute professional value, SGPropPortal provides the infrastructure for <span className="font-semibold text-[#001935]">Zero-Latency handshakes</span>.
              </p>
              <p>
                We protect the agent&apos;s signal, ensuring their expertise remains the focal point of every transaction. The platform is structured so professional judgment is not buried beneath duplicated inventory or diluted beneath generic marketplace clutter.
              </p>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="overflow-hidden rounded-[30px] border border-[#001935]/10 bg-white/86 shadow-[0_20px_60px_rgba(0,25,53,0.08)]">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  alt="Professional agent collaboration representing sovereignty and direct handshakes"
                  className="h-full w-full object-cover transition duration-500 hover:scale-[1.03]"
                  data-testid="about-agent-sovereignty-image"
                  src="https://images.pexels.com/photos/5582590/pexels-photo-5582590.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                />
              </div>
              <div className="border-t border-[#001935]/10 px-5 py-5">
                <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Zero-Latency handshake</p>
                <p className="mt-3 text-sm leading-7 text-[#42566f]">The infrastructure exists to keep the expert front and center, not hidden behind platform noise.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell py-8" data-testid="mvicto-architect-section">
        <div className="rounded-[32px] border border-[#001935]/10 bg-white/82 px-6 py-8 shadow-[0_18px_60px_rgba(0,25,53,0.06)] md:px-8 md:py-10">
          <div className="border-l-2 border-[#D4AF37] pl-6 py-2">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Meet the Architect — <a className={mvictoLinkClass} data-testid="about-mvicto-link-heading" href="https://mvicto.com" rel="noreferrer" target="_blank">Mvicto Technologies</a></p>
            <h2 className="mt-4 text-3xl font-semibold leading-tight text-[#001935] md:text-4xl">
              Built to process market logic, not just host market data.
            </h2>
            <div className="mt-6 space-y-5 text-sm leading-8 text-[#42566f] md:text-base">
              <p>
                SGPropPortal.com is engineered by <a className={mvictoLinkClass} data-testid="about-mvicto-link-body-1" href="https://mvicto.com" rel="noreferrer" target="_blank">Mvicto Technologies</a>, a firm specializing in agentic AI and business logic automation. Mvicto was commissioned to architect a portal that does more than host data—it processes it.
              </p>
              <p>
                By developing the proprietary logic for our AI tools, <a className={mvictoLinkClass} data-testid="about-mvicto-link-body-2" href="https://mvicto.com" rel="noreferrer" target="_blank">Mvicto Technologies</a> has bridged the gap between complex market analytics and intuitive user experience. Every automated workflow is designed to preserve the professional integrity of the agents.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell pt-8" data-testid="about-signature-cta-section">
        <div className="overflow-hidden rounded-[36px] border border-[#D4AF37]/18 bg-[linear-gradient(135deg,#07192f_0%,#102947_46%,#173656_100%)] px-8 py-10 text-center text-[#F9F8F6] shadow-[0_30px_90px_rgba(0,25,53,0.22)] md:px-10 md:py-12">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#D4AF37]">The Signature CTA</p>
          <h2 className="mt-4 text-4xl font-semibold leading-tight text-white md:text-5xl">Own the Future.</h2>
          <p className="mx-auto mt-5 max-w-3xl text-sm leading-8 text-[#e6dcc4] md:text-base">
            The Signal is Clear. The Seats are Limited. Secure your place within the new architecture of Singapore Real Estate.
          </p>
          <div className="mt-8 flex justify-center">
            <Link className="gold-button" data-testid="signature-cta-button" to="/pricing">
              Secure My Founding Seat <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
          <p className="mt-4 text-xs uppercase tracking-[0.18em] text-[#D4AF37]">Starting at SGD 129/mo. Zero commission splits. Total sovereignty.</p>
        </div>
      </section>
    </div>
  );
};